import { useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { minutesToMilliseconds } from "../helpers";
import { useDispatch, useSelector } from "react-redux";
import {
  EXTERNAL_ACCOUNT_SET_ACCOUNT,
  EXTERNAL_ACCOUNT_SET_TIMED_OUT,
  EXTERNAL_ACCOUNT_SET_TIMING_OUT,
  fetchApplicationAndDependencies,
} from "src/redux/actions";
import { removeScoutFromPage } from "src/services/scout";

const SWITCH_ACCOUNT_TIMEOUT = minutesToMilliseconds(14);
const SWITCH_ACCOUNT_FINAL_TIMEOUT = minutesToMilliseconds(1);

export const useIsSwitchAccountViewOnlyMode = () => {
  const externalAccount = useSelector(
    (state: any) => state?.externalAccount?.account
  );

  return !!externalAccount;
};

export function useSwitchAccount() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const switchAccountTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const switchAccountFinalTimeout = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  function navToDashboard() {
    if (location.pathname !== "/") {
      history.push("/");
    }
  }

  const initializeSwitchAccount = (account) => {
    // Make sure we are on the dashboard when switching
    navToDashboard();
    dispatch({ type: EXTERNAL_ACCOUNT_SET_ACCOUNT, account });
    // Setup timer to begin logout process in 14 minutes
    switchAccountTimeout.current = setTimeout(
      switchAccountTimeoutHandler,
      SWITCH_ACCOUNT_TIMEOUT
    );
    addSwitchAccountEventListeners();
    dispatch(fetchApplicationAndDependencies());
  };

  const clearSwitchAccount = () => {
    removeScoutFromPage();
    // Make sure we are on the dashboard when switching
    navToDashboard();
    // Clear redux state
    dispatch({ type: EXTERNAL_ACCOUNT_SET_ACCOUNT });

    // Clear event listeners, timers, session storage
    removeSwitchAccountEventListeners();
    clearTimeout(switchAccountTimeout.current);
    clearTimeout(switchAccountFinalTimeout.current);
    dispatch(fetchApplicationAndDependencies());
  };

  const switchAccountTimeoutHandler = () => {
    dispatch({ type: EXTERNAL_ACCOUNT_SET_TIMING_OUT, payload: true });
    switchAccountFinalTimeout.current = setTimeout(
      switchAccountFinalTimeoutHandler,
      SWITCH_ACCOUNT_FINAL_TIMEOUT
    );
  };

  const switchAccountFinalTimeoutHandler = () => {
    dispatch({ type: EXTERNAL_ACCOUNT_SET_TIMED_OUT, payload: true });
    clearSwitchAccount();
  };

  const addSwitchAccountEventListeners = () => {
    window.addEventListener("click", resetTimers);
    window.addEventListener("visibilitychange", handleVisibilityChange);
  };

  const removeSwitchAccountEventListeners = () => {
    window.removeEventListener("click", resetTimers);
    window.removeEventListener("visibilitychange", handleVisibilityChange);
  };

  const resetTimers = useCallback(() => {
    clearTimeout(switchAccountTimeout.current);
    clearTimeout(switchAccountFinalTimeout.current);
    switchAccountTimeout.current = setTimeout(
      switchAccountTimeoutHandler,
      SWITCH_ACCOUNT_TIMEOUT
    );
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState !== "visible") {
      switchAccountFinalTimeoutHandler();
    }
  }, []);

  return {
    initializeSwitchAccount,
    clearSwitchAccount,
  };
}
